import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeftOutlined, ArrowRightOutlined, DoubleRightOutlined, DoubleLeftOutlined } from '@ant-design/icons';

import Options from './Options';
import Info from './Info';
import { Button, CopyToClipboard, Row, Swiper, Text } from '../../../../common';
import { Img, useMenu } from '../../../common';
import { useBreakpoints } from '../../../../common/providers';

import watsAppIcon from '../../../../assets/icons/whatsapp-color.svg';
import viberIcon from '../../../../assets/icons/viber-color.svg';
import swipeRightIcon from '../../../../assets/icons/swipe-right.svg';
import swipeRightColorIcon from '../../../../assets/icons/swipe-right-color.svg';

import './styles.less';

const {
  REACT_APP_CLOUD_FRONT_URL,
  REACT_APP_CLIENT_URL,
  REACT_APP_SHOWROOM_PHONE_NUMBER
} = process.env;

const Product = props => {
  const { product, showLink, showBackButton } = props;
  const { photos, options } = product;
  const isAvailable = !!options.length;
  const { t } = useTranslation();
  const [displayPhoto, setDisplayPhoto] = useState(false);
  const { triggerMenu } = useMenu();
  const swiperRef = useRef();
  const { isMD1024 } = useBreakpoints();

  useEffect(() => {
    setTimeout(() => {
      setDisplayPhoto(true);
    }, 100);
  }, []);

  const goNext = useCallback(() => {
    swiperRef.current?.swiper.slideNext();
  }, []);

  const goPrev = useCallback(() => {
    swiperRef.current?.swiper.slidePrev();
  }, []);

  const text = useMemo(() =>
    t(
      'Hey, I\'d like to order this item {{link}}',
      { link: `${REACT_APP_CLIENT_URL}/product/${product.id}` }),
    [product.id, t]
  );

  const renderNavigationComponent = useCallback(() => {
    if (isMD1024) {
      return (
        <>
          <Row className="swiper-left-button">
            <Button shape="circle" icon={<DoubleLeftOutlined />} onClick={goPrev} />
          </Row>
          <Row className="swiper-right-button">
            <Button shape="circle" icon={<DoubleRightOutlined />} onClick={goNext} />
          </Row>
        </>
      );
    }

    return (
      <>
        <Row className="photo-info-icon">
          <Img size={40} src={swipeRightColorIcon} alt="swipe" style={{width: 40}} />
        </Row>
        <Row className="photo-info-icon">
          <Img size={40} src={swipeRightIcon} alt="swipe" style={{width: 40}} />
        </Row>
        <Text p white className="photo-info-text">
          {t`Swipe to see more`}
        </Text>
      </>
    );
  }, [goNext, goPrev, isMD1024, t]);

  return useMemo(() => (
    <Row full className="product-content-container">
      {showBackButton && (
        <Row className="back-button-container">
          <Button
            invisible
            style={{ padding: 0, marginBottom: 20 }}
            onClick={() => triggerMenu(`product-${product.id}`)}
          >
            <Row align="center">
              <ArrowLeftOutlined style={{ marginRight: 10 }} />
              <Text h4 white style={{ lineHeight: 1 }}>{t`Back to products`}</Text>
            </Row>
          </Button>
        </Row>
      )}

      <Row className="product-content">
        <Row className="product-photo-container">
          {displayPhoto && (
            <Swiper
              ref={swiperRef}
              pagination={{
                el: '.swiper-pagination',
                clickable: true,
                dynamicBullets: true
              }}
            >
              {photos.map(({ key }) => (
                <img
                  key={key}
                  src={`${REACT_APP_CLOUD_FRONT_URL}/${key}`}
                  alt="product item"
                />
              ))}
            </Swiper>
          )}
          <Row style={{ position: 'absolute', bottom: 15, right: 15, zIndex: 1 }}>
            <CopyToClipboard
              onCopy={() => {}}
              text={`${REACT_APP_CLIENT_URL}/product/${product.id}`}
            />
            {showLink && (
              <Link to={`/product/${product.id}`} style={{ marginLeft: 15 }}>
                <Button shape="circle" icon={<ArrowRightOutlined />} />
              </Link>
            )}
          </Row>
          {photos.length > 1 && renderNavigationComponent()}
        </Row>
        <Row className="product-info-container">
          <Info product={product} />
          <Options product={product} />
          {!isAvailable && (
            <Row direction="column">
              <Row mb={20}>
                <Text h4 white>{t`Contact us:`}</Text>
              </Row>
              <Row>
                <Row mr={30}>
                  <a href={`https://api.whatsapp.com/send?phone=${REACT_APP_SHOWROOM_PHONE_NUMBER}&text=${text}`}>
                    <Button invisible style={{ padding: 0, height: 'auto' }}>
                      <Img size={40} src={watsAppIcon} alt="whatsapp" />
                    </Button>
                  </a>
                </Row>
                <Row mr={30}>
                  <a href={`viber://chat?number=${REACT_APP_SHOWROOM_PHONE_NUMBER}&text=${text}`}>
                    <Button invisible style={{ padding: 0, height: 'auto' }}>
                      <Img size={40} src={viberIcon} alt="viber" />
                    </Button>
                  </a>
                </Row>
                {/*<Row>*/}
                {/*  <Button invisible style={{ padding: 0, height: 'auto' }}>*/}
                {/*    <Img size={40} src={telegramIcon} alt="watsapp" />*/}
                {/*  </Button>*/}
                {/*</Row>*/}
              </Row>
            </Row>
          )}
        </Row>
      </Row>
    </Row>
  ), [
    showBackButton,
    t,
    displayPhoto,
    photos,
    product,
    showLink,
    renderNavigationComponent,
    isAvailable,
    text,
    triggerMenu
  ]);
};

Product.propTypes = {
  product: PropTypes.object.isRequired,
  showLink: PropTypes.bool,
  showBackButton: PropTypes.bool
};

export default Product;